import { ValidationError } from "../errors";
import { sessionClock } from "../logic/session-clock";
import type {
  DeviceSession,
  IdentityApi,
  Profile,
  SecondFactor,
  SecurityStatus,
  TotpEnrolment,
} from "../types";
import { findOrg, initialsOf, requireSession, type Ctx } from "./context";
import type { StoredUser } from "./state";

/**
 * The signed-in person's own account: profile (FR-ADMIN-007, FR-PROJ-121), two-step status and devices
 * (FR-ADMIN-044, 048). Live: our identity service, which reads and revokes sessions through the WorkOS Sessions API.
 */

type AccountApi = Pick<
  IdentityApi,
  | "getProfile"
  | "updateProfile"
  | "getSecurity"
  | "enrolTotp"
  | "verifyTotp"
  | "removeSecondFactor"
  | "listSessions"
  | "revokeSession"
  | "revokeAllSessions"
>;

const NAME_MAX = 80;
/** The method the mock records for an authenticator app, as the seed names it. */
const TOTP_METHOD = "Authenticator app";
const TOTP_CODE = /^\d{6}$/;
/** A placeholder picture for the demo's QR code: a plain square, not a real code. */
const DEMO_QR =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCA4IDgnPjxyZWN0IHdpZHRoPSc4JyBoZWlnaHQ9JzgnLz48L3N2Zz4=";
const DEMO_SECRET = "DEMO DEMO DEMO DEMO";

/** The mock keeps one factor per person, named after its method. */
function factorsOf(userId: string, method: string | undefined, since: number): SecondFactor[] {
  if (method === undefined) return [];
  return [
    {
      id: `factor-${userId}`,
      kind: method === TOTP_METHOD ? "totp" : "other",
      createdAt: new Date(since).toISOString(),
    },
  ];
}

function profileOf(user: StoredUser): Profile {
  return {
    name: user.name,
    email: user.email,
    timeZone: user.timeZone,
    units: user.units,
    coaRegistration:
      user.coa === undefined
        ? undefined
        : { number: user.coa.number, validUntil: user.coa.validUntil },
  };
}

export function createAccountApi(ctx: Ctx): AccountApi {
  return {
    async getProfile(): Promise<Profile> {
      await ctx.wait();
      return profileOf(requireSession(ctx).user);
    },

    async updateProfile(patch) {
      await ctx.wait();
      const { user } = requireSession(ctx);
      if (patch.name !== undefined) {
        const name = patch.name.trim();
        if (name === "") throw new ValidationError("name", "Enter your name.");
        if (name.length > NAME_MAX)
          throw new ValidationError("name", `Use ${String(NAME_MAX)} characters or fewer.`);
        user.name = name;
        user.initials = initialsOf(name);
      }
      if (patch.timeZone !== undefined) user.timeZone = patch.timeZone;
      if (patch.units !== undefined) user.units = patch.units;
      if (patch.coaRegistration !== undefined) {
        const number = patch.coaRegistration.number.trim();
        if (number === "") delete user.coa;
        else
          user.coa = {
            number,
            ...(patch.coaRegistration.validUntil === undefined
              ? {}
              : { validUntil: patch.coaRegistration.validUntil }),
          };
      }
      ctx.commit();
      return profileOf(user);
    },

    async getSecurity(): Promise<SecurityStatus> {
      await ctx.wait();
      const { session, user } = requireSession(ctx);
      const org =
        session.organisationId === undefined ? undefined : findOrg(ctx, session.organisationId);
      return {
        twoStep: { enabled: user.twoStep !== undefined, method: user.twoStep },
        factors: factorsOf(user.id, user.twoStep, session.createdAt),
        requiredByOrganisation: org?.twoStepRequired ?? false,
        signInMethod: session.authMethod,
      };
    },

    async enrolTotp(): Promise<TotpEnrolment> {
      await ctx.wait();
      const { user } = requireSession(ctx);
      return {
        factorId: `factor-${user.id}`,
        challengeId: `challenge-${user.id}`,
        qrCode: DEMO_QR,
        secret: DEMO_SECRET,
      };
    },

    async verifyTotp(_enrolment, code) {
      await ctx.wait();
      if (!TOTP_CODE.test(code.replace(/\s/g, "")))
        throw new ValidationError("code", "Enter the 6-digit code your authenticator app shows.");
      const { user } = requireSession(ctx);
      user.twoStep = TOTP_METHOD;
      ctx.commit();
      return this.getSecurity();
    },

    async removeSecondFactor() {
      await ctx.wait();
      const { user } = requireSession(ctx);
      delete user.twoStep;
      ctx.commit();
      return this.getSecurity();
    },

    async listSessions(): Promise<DeviceSession[]> {
      await ctx.wait();
      const { session: current, user } = requireSession(ctx);
      const now = ctx.now();
      return ctx.state.sessions
        .filter(
          (s) =>
            s.userId === user.id &&
            s.endedAt === undefined &&
            sessionClock(s, ctx.options.limits, now) === "active",
        )
        .sort(
          (a, b) =>
            Number(b.id === current.id) - Number(a.id === current.id) ||
            b.lastActiveAt - a.lastActiveAt,
        )
        .map((s) => ({
          id: s.id,
          current: s.id === current.id,
          device: s.device,
          browser: s.browser,
          city: s.city,
          ipAddress: s.ip,
          authMethod: s.authMethod,
          createdAt: new Date(s.createdAt).toISOString(),
          lastActiveAt: new Date(s.lastActiveAt).toISOString(),
        }));
    },

    async revokeSession(sessionId) {
      await ctx.wait();
      const { session: current, user } = requireSession(ctx);
      const target = ctx.state.sessions.find((s) => s.id === sessionId && s.userId === user.id);
      if (target === undefined || target.endedAt !== undefined) return;
      target.endedAt = ctx.now();
      target.endReason = target.id === current.id ? "signed_out" : "revoked";
      if (target.id === current.id) delete ctx.state.cookie;
      ctx.commit();
    },

    async revokeAllSessions() {
      await ctx.wait();
      const { session: current, user } = requireSession(ctx);
      for (const s of ctx.state.sessions) {
        if (s.userId !== user.id || s.endedAt !== undefined) continue;
        s.endedAt = ctx.now();
        s.endReason = s.id === current.id ? "signed_out" : "revoked";
      }
      delete ctx.state.cookie;
      ctx.commit();
    },
  };
}
